import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaceBootstrap } from '@/hooks/useWorkspaceBootstrap';
import { useWorkspaceModules } from '@/hooks/useWorkspaceModules';
import { useAccessResolver as useAccessResolverHook } from '@/hooks/useAccessResolver';
import api from '@/lib/axios';

/**
 * Access Resolution Response from Backend
 * 
 * Expected response from GET /auth/resolve-access:
 * {
 *   accessType: 'system_admin' | 'workspace' | 'trial',
 *   workspace?: {
 *     id: string;
 *     slug: string;
 *     name: string;
 *     isTrial?: boolean;
 *   },
 *   redirectTo?: string; // Optional explicit redirect path
 * }
 */
export interface AccessResolution {
  accessType: 'system_admin' | 'workspace' | 'trial';
  workspace?: {
    id: string;
    slug: string;
    name: string;
    isTrial?: boolean;
  };
  redirectTo?: string;
}

interface AccessResolverContextValue {
  resolution: AccessResolution | null;
  isResolving: boolean;
  error: Error | null;
  isResolved: boolean;
  isBootstrapping: boolean;
  workspaces: any[];
  modules: string[];
  hasModule: (moduleKey: string) => boolean;
  isLoadingModules: boolean;
  // Access control
  permissions: Record<string, { read: boolean; write: boolean; admin: boolean }>;
  canWrite: (resource: string) => boolean;
  isAdmin: (resource?: string) => boolean;
  plan: { code: string; name: string; is_trial: boolean };
  isLoadingAccess: boolean;
}

const AccessResolverContext = createContext<AccessResolverContextValue | null>(null);

export const AccessResolverProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Workspace bootstrap hook
  const {
    workspaces,
    isLoading: isBootstrapping,
    shouldCreateTrial,
    bootstrap,
  } = useWorkspaceBootstrap();
  
  const [resolution, setResolution] = useState<AccessResolution | null>(null);
  const [isResolving, setIsResolving] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isResolved, setIsResolved] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(false);

  // Get workspace ID from resolution or first workspace
  const workspaceId = resolution?.workspace?.id || workspaces[0]?.id;

  // Fetch workspace modules AFTER workspace is resolved
  // Hook is called unconditionally, but enabled only when workspaceId exists
  const { modules, hasModule, isLoading: isLoadingModules } = useWorkspaceModules(workspaceId);

  // Fetch access information (modules, permissions, plan)
  // Hook is called unconditionally, but enabled only when workspaceId exists
  const {
    modules: accessModules,
    permissions,
    plan,
    canWrite: canWriteAccess,
    isAdmin: isAdminAccess,
    isLoading: isLoadingAccess,
  } = useAccessResolverHook(workspaceId);

  // Use access modules if available, otherwise fall back to workspace modules
  const finalModules = accessModules.length > 0 ? accessModules : modules;
  const finalHasModule = accessModules.length > 0 
    ? ((moduleKey: string) => accessModules.includes(moduleKey))
    : hasModule;

  /**
   * Public routes that don't require access resolution
   */
  const publicRoutes = [
    '/auth',
    '/',
    '/reset-password',
    '/reset-password/confirm',
    '/privacy-policy',
    '/terms',
    '/terms-of-service',
    '/onboarding/invite',
    '/invite',
    '/invite/accept',
    '/test-processing',
  ];

  const isPublicRoute = (path: string): boolean => {
    return publicRoutes.some(route => 
      path === route || 
      path.startsWith(route + '/') ||
      path.startsWith('/invite/') ||
      path.startsWith('/onboarding/invite/')
    );
  };

  /**
   * Bootstrap workspace if needed (create trial workspace if user has none)
   */
  const bootstrapWorkspace = useCallback(async () => {
    if (!user || isPublicRoute(location.pathname)) {
      setIsBootstrapped(true);
      return;
    }

    // If already bootstrapped, don't run again
    if (isBootstrapped) {
      return;
    }

    try {
      // Wait for workspaces to load
      if (isBootstrapping) {
        return; // Still loading, wait
      }

      // Check if user needs workspace bootstrap
      if (shouldCreateTrial) {
        // Create trial workspace
        await bootstrap();
        // Wait a bit for the workspace to be created and refetched
        // The bootstrap hook will handle navigation
        setIsBootstrapped(true);
      } else if (workspaces.length > 0) {
        // Workspaces exist, bootstrap is complete
        setIsBootstrapped(true);
      } else {
        // No workspaces and not creating - bootstrap complete (will be handled by access resolution)
        setIsBootstrapped(true);
      }
    } catch (err) {
      console.error('Workspace bootstrap failed:', err);
      // Continue anyway - access resolution will handle fallback
      setIsBootstrapped(true);
    }
  }, [user, shouldCreateTrial, isBootstrapping, workspaces, bootstrap, location.pathname, isBootstrapped]);

  /**
   * Resolve user access on app load
   */
  const resolveAccess = useCallback(async () => {
    // Don't resolve if not authenticated
    if (!user) {
      setIsResolving(false);
      setIsResolved(true);
      return;
    }

    // Don't resolve for public routes
    if (isPublicRoute(location.pathname)) {
      setIsResolving(false);
      setIsResolved(true);
      return;
    }

    // Don't re-resolve if already resolved (prevents infinite loops)
    if (isResolved && resolution) {
      return;
    }

    // Wait for workspace bootstrap to complete
    if (!isBootstrapped) {
      return;
    }

    try {
      setIsResolving(true);
      setError(null);

      // Try to call the access resolution endpoint
      // If it doesn't exist (404), fall back to workspace-based resolution
      let data: AccessResolution | null = null;
      try {
        const response = await api.get<AccessResolution>('/auth/resolve-access');
        data = response.data;
      } catch (apiError: any) {
        // If endpoint doesn't exist (404), use workspace bootstrap data instead
        if (apiError.response?.status === 404) {
          // This is expected when the endpoint isn't implemented yet - use fallback silently
          // Only log in development for debugging
          if (process.env.NODE_ENV === 'development') {
            console.warn('Access resolution endpoint not available, using workspace bootstrap data');
          }
          // Create a resolution object from workspace data
          if (workspaces.length > 0) {
            const firstWorkspace = workspaces[0];
            data = {
              accessType: 'workspace',
              workspace: {
                id: firstWorkspace.id,
                slug: firstWorkspace.slug,
                name: firstWorkspace.name,
                isTrial: firstWorkspace.plan_code === 'trial',
              },
            };
          } else {
            // No workspaces - will be handled by bootstrap
            data = {
              accessType: 'trial',
            };
          }
        } else {
          // For other errors, rethrow
          throw apiError;
        }
      }

      if (data) {
        setResolution(data);
        setIsResolved(true);

        // Only redirect if we're not already on the target route
        const currentPath = location.pathname;
        
        // Handle redirects based on access type
        if (data.redirectTo && currentPath !== data.redirectTo) {
          // Explicit redirect path from backend
          navigate(data.redirectTo, { replace: true });
        } else {
          // Default redirect logic - only redirect if not already on target
          switch (data.accessType) {
            case 'system_admin':
              if (!currentPath.startsWith('/system')) {
                navigate('/system', { replace: true });
              }
              break;
            
            case 'workspace':
              if (data.workspace?.slug && !currentPath.startsWith(`/${data.workspace.slug}`)) {
                navigate(`/${data.workspace.slug}`, { replace: true });
              } else if (workspaces.length > 0) {
                // Fallback: use first workspace from bootstrap
                const firstWorkspace = workspaces[0];
                if (firstWorkspace && !currentPath.startsWith(`/${firstWorkspace.slug}`)) {
                  navigate(`/${firstWorkspace.slug}`, { replace: true });
                }
              }
              break;
            
            case 'trial':
              // Trial users get auto-created workspace
              if (data.workspace?.slug && !currentPath.startsWith(`/${data.workspace.slug}`)) {
                navigate(`/${data.workspace.slug}`, { replace: true });
              } else if (workspaces.length > 0) {
                // Use workspace from bootstrap
                const firstWorkspace = workspaces[0];
                if (firstWorkspace && !currentPath.startsWith(`/${firstWorkspace.slug}`)) {
                  navigate(`/${firstWorkspace.slug}`, { replace: true });
                }
              } else if (currentPath !== '/select-workspace') {
                // If no workspace provided, create one or redirect to onboarding
                navigate('/select-workspace', { replace: true });
              }
              break;
            
            default:
              // Unknown access type, redirect to workspace selection
              if (workspaces.length > 0) {
                const firstWorkspace = workspaces[0];
                if (firstWorkspace && !currentPath.startsWith(`/${firstWorkspace.slug}`)) {
                  navigate(`/${firstWorkspace.slug}`, { replace: true });
                }
              } else if (currentPath !== '/select-workspace') {
                navigate('/select-workspace', { replace: true });
              }
          }
        }
      } else {
        // No resolution data - fall back to workspace-based redirect
        if (workspaces.length > 0) {
          const firstWorkspace = workspaces[0];
          if (firstWorkspace && !location.pathname.startsWith(`/${firstWorkspace.slug}`)) {
            navigate(`/${firstWorkspace.slug}`, { replace: true });
          }
        }
        setIsResolved(true);
      }
    } catch (err: any) {
      console.error('Access resolution failed:', err);
      setError(err instanceof Error ? err : new Error('Failed to resolve access'));
      
      // On error, try to redirect to first workspace if available
      if (workspaces.length > 0) {
        const firstWorkspace = workspaces[0];
        if (firstWorkspace && !location.pathname.startsWith(`/${firstWorkspace.slug}`)) {
          navigate(`/${firstWorkspace.slug}`, { replace: true });
        }
      }
      
      // On error, allow navigation but log the issue
      // User might still be able to access some routes
      setIsResolved(true);
    } finally {
      setIsResolving(false);
    }
  }, [user, navigate, location.pathname, isResolved, resolution, isBootstrapped, workspaces]);

  /**
   * Bootstrap workspace and resolve access when user changes or on mount
   */
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Step 1: Bootstrap workspace (create trial if needed)
    bootstrapWorkspace();
  }, [authLoading, user, bootstrapWorkspace]);

  /**
   * Step 2: Resolve access after bootstrap completes
   */
  useEffect(() => {
    if (authLoading || !isBootstrapped) {
      return;
    }

    resolveAccess();
  }, [authLoading, isBootstrapped, resolveAccess]);

  const value: AccessResolverContextValue = {
    resolution,
    isResolving: isResolving || isBootstrapping,
    error,
    isResolved,
    isBootstrapping,
    workspaces,
    modules: finalModules || [], // Never undefined, always array
    hasModule: finalHasModule || (() => false), // Default to false if not available
    isLoadingModules: isLoadingModules || isLoadingAccess,
    permissions: permissions || {}, // Never undefined, always object
    canWrite: canWriteAccess || (() => false), // Default to false if not available
    isAdmin: isAdminAccess || (() => false), // Default to false if not available
    plan: plan || { code: 'trial', name: 'Trial', is_trial: true }, // Never undefined
    isLoadingAccess,
  };

  return (
    <AccessResolverContext.Provider value={value}>
      {children}
    </AccessResolverContext.Provider>
  );
};

export const useAccessResolver = () => {
  const ctx = useContext(AccessResolverContext);
  if (!ctx) {
    throw new Error('useAccessResolver must be used inside AccessResolverProvider');
  }
  return ctx;
};

